import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './use-auth';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';

interface SyncStatus {
  isActive: boolean;
  progress?: number;
  lastSync?: Date;
}

interface PhotoChange {
  type: 'added' | 'deleted';
  photoId?: number;
  photo?: any;
}

interface UseSyncResult {
  syncStatus: SyncStatus;
  recentChanges: PhotoChange[];
  triggerSync: () => void;
  isConnected: boolean;
}

/**
 * Enterprise-grade real-time sync hook
 * Runs completely in the background with no UI
 */
export function useRealtimeSync(surveyId?: number): UseSyncResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);

  // These states are maintained for API compatibility but won't be shown to users
  const [isConnected, setIsConnected] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isActive: false,
    progress: undefined,
    lastSync: undefined,
  });
  const [recentChanges, setRecentChanges] = useState<PhotoChange[]>([]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!user?.id) return;

    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      auth: {
        userId: user.id,
      },
    });

    socketRef.current = socket;

    // Connection handlers
    socket.on('connect', () => {
      console.log('[Realtime Sync] Connected to server');
      setIsConnected(true);

      // Authenticate
      socket.emit('authenticate', { userId: user.id });
    });

    socket.on('disconnect', () => {
      console.log('[Realtime Sync] Disconnected from server');
      setIsConnected(false);
    });

    socket.on('authenticated', () => {
      console.log('[Realtime Sync] Authenticated successfully');

      // Subscribe to survey if ID provided
      if (surveyId) {
        socket.emit('subscribe:survey', surveyId);
      }
    });

    // Sync event handlers
    socket.on('sync:start', (data) => {
      if (data.surveyId === surveyId) {
        setSyncStatus((prev) => ({ ...prev, isActive: true, progress: 0 }));
      }
    });

    socket.on('sync:progress', (data) => {
      if (data.surveyId === surveyId) {
        setSyncStatus((prev) => ({ ...prev, progress: data.progress }));
      }
    });

    socket.on('sync:complete', (data) => {
      if (data.surveyId === surveyId) {
        setSyncStatus({
          isActive: false,
          progress: 100,
          lastSync: new Date(),
        });

        // Silent sync - no toast notifications
        const { photosDeleted, photosAdded } = data.result;
        console.log(`[Background Sync] Completed: ${photosDeleted} deleted, ${photosAdded} added`);

        // Invalidate queries to refresh UI
        queryClient.invalidateQueries({ queryKey: ['site-survey-photos', surveyId] });
      }
    });

    socket.on('sync:error', (data) => {
      if (data.surveyId === surveyId) {
        setSyncStatus((prev) => ({ ...prev, isActive: false }));
        // Silent error handling - just log it
        console.error(`[Background Sync] Error: ${data.error}`);
      }
    });

    // Photo change handlers
    socket.on('photo:deleted', (data) => {
      if (data.surveyId === surveyId) {
        setRecentChanges((prev) => [
          { type: 'deleted', photoId: data.photoId },
          ...prev.slice(0, 9), // Keep last 10 changes
        ]);

        // Update UI immediately
        queryClient.setQueryData(['site-survey-photos', surveyId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            photos: old.photos?.filter((p: any) => p.id !== data.photoId) || [],
          };
        });
      }
    });

    socket.on('photo:added', (data) => {
      if (data.surveyId === surveyId) {
        setRecentChanges((prev) => [{ type: 'added', photo: data.photo }, ...prev.slice(0, 9)]);

        // Invalidate to refetch with new photo
        queryClient.invalidateQueries({ queryKey: ['site-survey-photos', surveyId] });
      }
    });

    // Keep-alive ping
    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping');
      }
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(pingInterval);
      if (surveyId) {
        socket.emit('unsubscribe:survey', surveyId);
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id, surveyId, queryClient]);

  // Trigger manual sync
  const triggerSync = useCallback(() => {
    if (!socketRef.current || !surveyId) return;

    socketRef.current.emit('sync:survey', surveyId);
    setSyncStatus((prev) => ({ ...prev, isActive: true, progress: 0 }));
  }, [surveyId]);

  return {
    syncStatus,
    recentChanges,
    triggerSync,
    isConnected,
  };
}

/**
 * Hook for global sync status
 */
export function useGlobalSyncStatus() {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>({});
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('authenticate', { userId: user.id });
    });

    socket.on('job:progress', (data) => {
      setStats((prev: any) => ({
        ...prev,
        activeJobs: {
          ...prev.activeJobs,
          [data.jobId]: data.progress,
        },
      }));
    });

    socket.on('job:completed', (data) => {
      setStats((prev: any) => {
        const { [data.jobId]: _, ...activeJobs } = prev.activeJobs || {};
        return {
          ...prev,
          activeJobs,
          lastCompleted: new Date(),
        };
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]);

  return stats;
}
